<script setup>
  import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
  import Button from "@/base-components/Button";
  import { uri } from "@/constants/config";
  import { onMounted, ref } from "vue";
  import { useFetch } from "@/composables/useFetch";
  //import chipIcon from "/image/chip.png";
  import { useI18n } from "vue-i18n";
  import { useNumberFormat } from "@/composables/useNumberFormat.js";
  import BaseCard from "@/base-components/Card/BaseCard.vue";
  import Vue3Datatable from "@bhplugin/vue3-datatable";
  import moment from "moment-jalaali";
  import BasePagination from "@/base-components/Table/BasePagination.vue";

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    wallet: {
      required: true,
    },
  });

  const { t: $t } = useI18n();
  const emit = defineEmits(["close", "onSuccess"]);

  function close() {
    emit("close");
  }

  function onSuccess() {
    emit("onSuccess");
  }

  const { formatNumber } = useNumberFormat();
  const data = null;
  const date = "";
  const walletHistory = ref(null);
  const profile = ref(null);
  const loadingWalletHistoryFlag = ref(true);
  const pageOptions = ref({
    count: 1,
    limit: 10,
    page: 1,
  });
  const cols = [
    { field: "counter", title: "#" },
    { field: "creator", title: $t("title.user") },
    { field: "amount", title: $t("title.wallet-amount") },
    { field: "action", title: $t("title.wallet-action") },
    { field: "comment", title: $t("title.comment") },
    { field: "balance", title: $t("title.wallet-balance") },
    { field: "date", title: $t("title.date") },
  ];

  const fetchWalletHistory = async () => {
    loadingWalletHistoryFlag.value = true;
    try {
      const { data, error } = await useFetch(
        `${uri.admin.financial.wallet.history}/${props.wallet.User.id}/histories?page=${pageOptions.value.page}`,
        { method: "GET" }
      );
      if (!error) {
        walletHistory.value = data;
        pageOptions.value.count = data.meta.total;
        pageOptions.value.limit = data.meta.perPage;
      }
    } catch (err) {
      console.error(err);
    } finally {
      loadingWalletHistoryFlag.value = false;
    }
  };
  onMounted(async () => {
    try {
      // Run both functions in parallel
      await Promise.all([fetchWalletHistory()]);
    } catch (err) {
      console.error("Error during data fetching:", err);
    }
  });
</script>

<template>
  <section>
    <BaseDialog
      :size="'w-[95vw]'"
      staticBackdrop
      :open="show"
      :has-footer="true"
      :title="$t('title.wallet')"
      @close="close"
    >
      <BaseCard
        class="col-span-12 sm:col-span-12 md:col-span-12 lg:col-span-12"
      >
        <template v-slot:body>
          <vue3-datatable
            :columns="cols"
            :rows="walletHistory?.data"
            :loading="loadingWalletHistoryFlag"
            :totalRows="pageOptions.count"
            :isServerMode="true"
            :pageSize="pageOptions.limit"
            :pagination="false"
            :sortable="false"
            class="advanced-table text-start items-start justify-start"
            rowClass="cursor-pointer"
          >
            <!-- Empty header slot to remove the header -->
            <template #header>
              <!-- No content here will hide the header -->
            </template>
            <!-- Empty footer slot to remove the footer -->
            <template #footer>
              <!-- No content here will hide the footer -->
            </template>

            <template #counter="data, key">
              <div class="text-start">
                {{
                  walletHistory?.data.indexOf(data.value) +
                  1 +
                  (pageOptions.page - 1) * pageOptions.limit
                }}
              </div>
            </template>
            <template #creator="data, key">
              <div class="text-start">
                {{
                  `${data.value.Creator?.Profile?.firstName} ${data.value.Creator?.Profile?.lastName}`
                }}
              </div>
            </template>
            <template #amount="data, key">
              <div class="text-start">
                {{ formatNumber(data.value.amount) }}
              </div>
            </template>
            <template #balance="data, key">
              <div class="text-start">
                {{ formatNumber(data.value.balance) }}
              </div>
            </template>
            <template #date="data, key">
              <div class="text-start" dir="ltr">
                {{
                  moment(data.value.createdAt)
                    .locale("fa")
                    .format("jYYYY/jMM/jDD HH:mm:ss")
                }}
              </div>
            </template>
            <template #action="data, key">
              <div class="text-start" v-if="!loadingWalletHistoryFlag">
                <Button
                  :variant="
                    data.value.action === 'ACTION_WITHDRAW'
                      ? 'outline-warning'
                      : 'outline-success'
                  "
                  size="sm"
                  class="inline-block w-16 mb-2 mr-1"
                >
                  {{
                    data.value.action === "ACTION_WITHDRAW"
                      ? $t("title.withdraw")
                      : $t("title.deposit")
                  }}
                </Button>
              </div>
            </template>
          </vue3-datatable>
          <div
            class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-start text-slate-500"
          >
            <div>
              <BasePagination
                v-if="pageOptions.count > pageOptions.limit"
                :page-options="pageOptions"
                @page-change="fetchWalletHistory"
              />
            </div>
          </div>
        </template>
      </BaseCard>
    </BaseDialog>
  </section>
</template>
